<template>
  <avs-card class="transaction-detail-container">
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop: toolbar + stats -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <!-- Filtres -->
          <v-row class="row mb-4">
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                @input="onSearchChange"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="search-field"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-menu
                v-model="menuStartDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="startDate"
                    :label="$t('Date de début')"
                    prepend-inner-icon="mdi-calendar-start"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" md="4">
              <v-menu
                v-model="menuEndDate"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="230px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="endDate"
                    :label="$t('Date de fin')"
                    prepend-inner-icon="mdi-calendar-end"
                    outlined
                    dense
                    hide-details
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  @input="applyFilters"
                  color="primary"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="applyFilters"
                item-text="name"
                item-value="id"
                :label="$t('Produits')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedShop"
                clearable
                @input="applyFilters"
                :items="shops"
                item-text="name"
                item-value="id"
                :label="$t('Stations')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-map-marker"
              />
            </v-col>

            <!-- Actions d'export -->
            <v-col cols="12" md="4" class="d-flex align-center justify-end">
              <v-btn
                color="success"
                outlined
                small
                class="export-btn mr-2"
                @click="exportToExcel"
              >
                <v-icon small left>mdi-file-excel</v-icon>
                Excel
              </v-btn>
              <v-btn
                color="error"
                outlined
                small
                class="export-btn mr-2"
                @click="exportToPdf"
              >
                <v-icon small left>mdi-file-pdf-box</v-icon>
                PDF
              </v-btn>
              <v-btn
                color="primary"
                outlined
                small
                class="export-btn"
                @click="stationDialog = true"
              >
                <v-icon small left>mdi-calendar-range</v-icon>
                Station
              </v-btn>
            </v-col>
          </v-row>

          <!-- Statistiques / Totaux -->
          <v-row class="stats-section">
            <v-col cols="12">
              <div class="stats-wrapper">
                <div class="stats-container">
                  <!-- Carte Total Général -->
                  <v-card class="stat-card stat-card-main" elevation="4">
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-mobile">{{ $t('Montant total') }}</div>
                        <div class="stat-value-mobile">
                          {{ formatNumber(totalAmount) }}
                          <span class="stat-unit-mobile">XOF</span>
                        </div>
                      </div>
                      <div class="stat-icon-mobile">
                        <v-icon size="40" color="white">mdi-cash-multiple</v-icon>
                      </div>
                    </div>
                    <v-divider class="my-0" style="border-color: rgba(255,255,255,0.3);" />
                    <div class="stat-secondary mt-2">
                      <v-icon small color="white">mdi-gas-station</v-icon>
                      {{ formatNumber(totalQuantity) }} L
                    </div>
                  </v-card>

                  <!-- Cartes par produit -->
                  <v-card
                    v-for="(item, index) in totalByProduct"
                    :key="index"
                    class="stat-card stat-card-product bgTable"
                    elevation="3"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div>
                        <div class="stat-label-mobile">{{ item.name }}</div>
                        <div class="stat-value-mobile">
                          {{ formatNumber(item.total) }}
                          <span class="stat-unit-mobile">XOF</span>
                        </div>
                      </div>
                      <div class="stat-icon-mobile">
                        <v-icon size="40">mdi-cash-multiple</v-icon>
                      </div>
                    </div>
                    <div class="stat-secondary-product">
                      <v-icon x-small>mdi-gauge</v-icon>
                      {{ formatNumber(item.total_liters) }} L
                    </div>
                  </v-card>
                </div>
              </div>
            </v-col>
          </v-row>

        </v-container>
      </div>

      <!-- Mobile: header + filtres + stats -->
      <div v-else class="col-12 mobile-header">
        <!-- Titre mobile -->
        <div class="mobile-page-header px-4 pt-4 pb-3">
          <div class="d-flex align-center mb-3">
            <v-icon large color="primary" class="mr-2">mdi-chart-line</v-icon>
            <div>
              <h3 class="text-h6 font-weight-bold mb-0">
                {{ $t('Transactions fidélité') }}
              </h3>
            </div>
          </div>
        </div>

        <!-- Filtres mobiles -->
        <div class="mobile-filters px-4">
          <v-text-field
            v-model="search"
            @input="onSearchChange"
            clearable
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            :label="$t('commons.search')"
            class="mb-3"
          />

          <v-menu
            v-model="menuStartDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="startDate"
                :label="$t('Date de début')"
                prepend-inner-icon="mdi-calendar-start"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="mb-3"
              />
            </template>
            <v-date-picker v-model="startDate" @input="applyFilters" />
          </v-menu>

          <v-menu
            v-model="menuEndDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="endDate"
                :label="$t('Date de fin')"
                prepend-inner-icon="mdi-calendar-end"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                class="mb-3"
              />
            </template>
            <v-date-picker v-model="endDate" @input="applyFilters" />
          </v-menu>

          <v-autocomplete
            v-model="selectedProduct"
            clearable
            :items="products"
            @input="applyFilters"
            item-text="name"
            item-value="id"
            :label="$t('Produits')"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-gas-station"
            class="mb-3"
          />

          <v-autocomplete
            v-model="selectedShop"
            clearable
            @input="applyFilters"
            :items="shops"
            item-text="name"
            item-value="id"
            :label="$t('Stations')"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-map-marker"
            class="mb-3"
          />

          <!-- Boutons d'export mobile -->
          <v-row dense class="mb-3">
            <v-col cols="4">
              <v-btn
                block
                color="success"
                outlined
                x-small
                @click="exportToExcel"
              >
                <v-icon small>mdi-file-excel</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                color="error"
                outlined
                x-small
                @click="exportToPdf"
              >
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="4">
              <v-btn
                block
                color="primary"
                outlined
                x-small
                @click="stationDialog = true"
              >
                <v-icon small>mdi-calendar-range</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Stats mobiles -->
        <div class="mobile-stats px-4 pb-3">
          <v-card class="stat-card-mobile mb-3" elevation="3">
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-3">
                <div>
                  <div class="stat-label-mobile">{{ $t('Montant total') }}</div>
                  <div class="stat-value-mobile">
                    {{ formatNumber(totalAmount) }}
                    <span class="stat-unit-mobile">XOF</span>
                  </div>
                </div>
                <div class="stat-icon-mobile">
                  <v-icon size="40" color="white">mdi-cash-multiple</v-icon>
                </div>
              </div>
              <v-divider class="mb-3" />
              <div class="stat-secondary-mobile">
                <v-icon small color="primary">mdi-gas-station</v-icon>
                <strong>{{ formatNumber(totalQuantity) }} L</strong>
              </div>
            </v-card-text>
          </v-card>

          <!-- Produits en chips -->
          <div class="d-flex flex-wrap" style="gap: 8px;">
            <v-chip
              v-for="(item, index) in totalByProduct"
              :key="index"
              small
              label
              color="accent"
              text-color="white"
            >
              <v-icon left x-small>mdi-barrel</v-icon>
              {{ item.name }}: {{ formatNumber(item.total) }} XOF
            </v-chip>
          </div>

        </div>
      </div>
    </template>

    <!-- ===== CONTENU ===== -->
    <template v-slot:content>
      <!-- Desktop: tableau -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-data-table
          class="ma-6 bgTable"
          :headers="headers"
          :loading-text="$t('Chargement des données')"
          :no-results-text="$t('Aucune donnée disponible')"
          :loading="isLoading"
          :items="items"
          :items-per-page="-1"
          hide-default-footer
          item-key="id"
          :item-class="rowClass"
          @dblclick:row="
            ($event, { item }) => {
              if (allow.duplicate(item)) {
                handleOpenEditDialog(item)
              }
            }
          "
        >
          <template v-slot:item.phone_number="{ item }">
            <div class="d-flex flex-column">
              <div class="font-weight-bold">
                {{ getUserOrVehicleContact(item) }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                {{ getUserOrDriverName(item) }}
              </div>
            </div>
          </template>

          <template v-slot:item.created_by.last_name="{ item }">
            {{ getUserOrDriverName(item) }}
          </template>

          <template v-slot:item.amount="{ item }">
            {{ formatNumber(item.amount) }}
          </template>

          <template v-slot:item.quantity="{ item }">
            {{ formatNumber(item.quantity) }}
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ toDatetime(item.created_at) }}
          </template>

          <template v-slot:item.product="{ item }">
            <div class="d-flex flex-column align-center">
              <v-chip small label color="accent" text-color="white">
                <v-icon left x-small>mdi-gas-station</v-icon>
                {{ item.product || 'N/A' }}
              </v-chip>
              <v-tooltip v-if="isBusiness && item.fraud_details && item.fraud_details.product_auto_corrected" bottom>
                <template #activator="{ on }">
                  <v-chip v-on="on" x-small color="light-green lighten-4" class="mt-1 px-1">
                    <v-icon x-small color="light-green darken-2" left>mdi-auto-fix</v-icon>
                    <span class="light-green--text text--darken-2" style="font-size:10px">Auto-corrigé</span>
                  </v-chip>
                </template>
                <span>Produit corrigé automatiquement par OCR</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <div class="d-flex align-center justify-center">
              <template v-if="isBusiness">
                <template v-if="item.fraud_details && item.fraud_details.ocr_completed">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-icon
                        v-on="on"
                        class="mr-1"
                        :color="item.fraud_details.ocr_amount_match ? 'success' : 'error'"
                      >
                        {{ item.fraud_details.ocr_amount_match ? 'mdi-cash-check' : 'mdi-cash-remove' }}
                      </v-icon>
                    </template>
                    <span>
                      {{ item.fraud_details.ocr_amount_match ? 'Montant OCR correspond' : 'Écart montant OCR' }}
                      <span v-if="item.fraud_details.ocr_closest_amount">
                        ({{ item.fraud_details.ocr_closest_amount }} XOF, écart {{ item.fraud_details.ocr_amount_deviation_pct }}%)
                      </span>
                    </span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-icon
                        v-on="on"
                        :color="item.fraud_details.ocr_product_match ? 'success' : 'warning'"
                      >
                        {{ item.fraud_details.ocr_product_match ? 'mdi-fuel' : 'mdi-alert-circle-outline' }}
                      </v-icon>
                    </template>
                    <span>
                      Produit : {{ item.fraud_details.ocr_product_match ? 'Trouvé sur la pompe' : 'Non détecté' }}
                    </span>
                  </v-tooltip>
                </template>
                <v-tooltip v-else-if="item.image && (!item.fraud_details || !item.fraud_details.ocr_completed)" bottom>
                  <template #activator="{ on }">
                    <v-icon v-on="on" color="grey lighten-1">mdi-text-search</v-icon>
                  </template>
                  <span>OCR en attente</span>
                </v-tooltip>
              </template>
              <component v-else :is="handleStatusComponent(item)" />
            </div>
          </template>

          <template v-slot:item.shop="{ item }">
            <div class="d-flex flex-column">
              <div class="font-weight-bold">
                {{ item.shop }}
              </div>
              <div class="grey--text text--darken-1 text-caption">
                #{{ item.transaction_number }}
              </div>
            </div>
          </template>

          <template v-slot:item.actions="{ item }">
            <div class="d-flex justify-center">
              <avs-button
                icon
                color="secondary"
                :iconI="'mdi-eye'"
                label
                fab
                small
                @click.stop="openItemUrl(item)"
              />
            </div>
          </template>
        </v-data-table>

        <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- Mobile: cartes -->
      <div v-else class="col-12 px-4">
        <v-skeleton-loader
          v-if="isLoading && !items.length"
          type="image, article, actions"
          class="mb-3"
        />
        <v-alert
          v-else-if="!items.length"
          type="info"
          dense
          outlined
          class="mb-3"
        >
          {{ $t('messages.information.no_data') }}
        </v-alert>

        <v-card
          v-for="it in items"
          :key="it.id"
          class="mb-3 transaction-card"
          :class="{ 'fidelity-refund-card': it.voucher_fidelity }"
          outlined
        >
          <v-card-title class="py-3 d-flex align-center">
            <div class="tx-title">
              <div class="company text-truncate">
                {{ getUserOrVehicleContact(it) || '—' }}
              </div>
              <div class="sub grey--text text--darken-1">
                {{ getUserOrDriverName(it) }}
              </div>
            </div>

            <v-spacer />

            <div class="d-flex align-center">
              <template v-if="isBusiness">
                <template v-if="it.fraud_details && it.fraud_details.ocr_completed">
                  <v-icon
                    small
                    class="mr-1"
                    :color="it.fraud_details.ocr_amount_match ? 'success' : 'error'"
                  >
                    {{ it.fraud_details.ocr_amount_match ? 'mdi-cash-check' : 'mdi-cash-remove' }}
                  </v-icon>
                  <v-icon
                    small
                    :color="it.fraud_details.ocr_product_match ? 'success' : 'warning'"
                  >
                    {{ it.fraud_details.ocr_product_match ? 'mdi-fuel' : 'mdi-alert-circle-outline' }}
                  </v-icon>
                </template>
                <v-icon v-else-if="it.image && (!it.fraud_details || !it.fraud_details.ocr_completed)" small color="grey lighten-1">
                  mdi-text-search
                </v-icon>
              </template>
              <component v-else :is="handleStatusComponent(it)" />
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3">
            <div class="grid-2">
              <div class="cell">
                <div class="label">{{ $t('Coûts (XOF)') }}</div>
                <div class="value">{{ formatNumber(it.amount) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Quantité (L)') }}</div>
                <div class="value">{{ formatNumber(it.quantity) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Produit') }}</div>
                <div class="value">{{ it.product || 'N/A' }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Date') }}</div>
                <div class="value">{{ toDatetime(it.created_at) }}</div>
              </div>
              <div class="cell">
                <div class="label">{{ $t('Effectué par') }}</div>
                <div class="value">{{ it.shop || 'N/A' }}</div>
              </div>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions class="py-2 d-flex align-center">
            <v-btn
              icon
              color="secondary"
              :title="$t('Voir la preuve')"
              class="mx-1"
              @click.stop="openItemUrl(it)"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- Dialog export station -->
      <v-dialog
        v-model="stationDialog"
        max-width="400px"
        :fullscreen="$vuetify.breakpoint.xs"
        transition="dialog-bottom-transition"
      >
        <v-card>
          <v-card-title class="headline d-flex align-center">
            <v-icon left color="primary">mdi-calendar-range</v-icon>
            Choisir la date
          </v-card-title>
          <v-card-text>
            <v-menu
              v-model="stationMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="stationDate"
                  label="Date de station"
                  prepend-inner-icon="mdi-calendar"
                  outlined
                  readonly
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="stationDate"
                @input="stationMenu = false"
              />
            </v-menu>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="stationDialog = false">{{ $t('commons.cancel') }}</v-btn>
            <v-btn color="primary" @click="exportStation">Exporter</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Overlays d'export (style identique à TransactionDetail) -->
      <v-overlay :value="isExportingPdf" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular
            indeterminate
            size="64"
            width="6"
            color="error"
          />
          <div class="mt-4 font-weight-bold">Génération du PDF...</div>
        </div>
      </v-overlay>

      <v-overlay :value="isExportingExcel" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular
            indeterminate
            size="64"
            width="6"
            color="success"
          />
          <div class="mt-4 font-weight-bold">Génération de l'Excel...</div>
        </div>
      </v-overlay>

      <v-overlay :value="isExportingStation" absolute opacity="0.9" z-index="9999">
        <div class="export-overlay-content">
          <v-progress-circular
            indeterminate
            size="64"
            width="6"
            color="primary"
          />
          <div class="mt-4 font-weight-bold">Préparation de l'export station...</div>
        </div>
      </v-overlay>
    </template>
  </avs-card>
</template>

<script>
import { toDatetime, toDate } from "@/utils/datetime"
import { Accepted, Canceled, Pending } from "@/components/generics/status"
import { debounce } from "lodash"
import {
  TransactionDetailService,
  ProductDetailService,
  ShopDetailService
} from "@/services"
import { mkUnique } from "@/utils/validator"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

export default {
  name: "FidelityDetail",
  components: { Accepted, Canceled, Pending },
  created() {
    const q = this.$route.query.search;
    if (typeof q === "string" && q.trim() !== "") {
      this.search = q;
    }
    this.fetchFieldTypes()
    this.fetchProductsTypes()
    this.fetchShopTypes()
  },
  computed: {
    isBusiness() {
      return this.$store.state.app.company?.company_type === 'Business'
    },
    hasUniqueName: {
      get() {
        const names = this.items
          .filter(e => e.name !== this.selectedItem?.name)
          .map(e => e.name)
        return mkUnique(names, "name")
      }
    },
    department() {
      return this.$store.state.app.project
    }
  },
  data() {
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return {
      menu: false,
      page: 1,
      isLastPage: false,
      isFetchingMore: false,
      headers: [
        { text: this.$t("N° Client / Matricule"), value: "phone_number", sortable: true },
        //{ text: this.$t("Nom & Prénom"), value: "created_by.last_name", sortable: true },
        { text: this.$t("Coûts (XOF)"), value: "amount", sortable: true, filterable: false },
        { text: this.$t("Produit"), value: "product", align: "center", sortable: true, filterable: false },
        { text: this.$t("Quantité (L)"), value: "quantity", align: "center", sortable: true, filterable: false },
        { text: this.$t("Servi le"), value: "created_at", sortable: true, filterable: false },
        { text: this.$t("commons.status.name"), value: "status", align: "center", sortable: false, filterable: false },
        { text: this.$t("Effectué par"), value: "shop", sortable: true, filterable: false },
        { text: this.$t("Preuve"), value: "actions", align: "center", sortable: false, filterable: false }
      ],
      isEditable: true,
      items: [],
      search: "",
      companies: [],
      shops: [],
      products: [],
      isExportingPdf: false,
      isExportingExcel: false,
      isExportingStation: false,

      totalByProduct: [],
      startDate: this.formatDateToString(firstDayOfMonth),
      endDate: this.formatDateToString(lastDayOfMonth),
      selectedProduct: null,
      selectedCompany: null,
      selectedShop: null,
      menuStartDate: false,
      menuEndDate: false,
      totalAmount: 0,
      totalQuantity: 0,
      isLoading: false,
      getTransactionDebounced: null,

      deleteDialogIsOpen: false,
      editDialogIsOpen: false,
      editDialogContent: null,
      stationDialog: null,
      selectedItem: undefined,
      stationMenu: false,
      stationDate: this.formatDateToString(new Date())
    }
  },
  methods: {
    toDatetime,
    toDate,
    rowClass(row) {
      const item = row && row.item ? row.item : row
      const classes = []
      if (item?.voucher_fidelity) classes.push("fidelity-refund")
      if (this.isBusiness) {
        const fd = item?.fraud_details
        if (fd && fd.product_auto_corrected) {
          classes.push("ocr-auto-corrected")
        } else if (fd && fd.ocr_completed && (!fd.ocr_amount_match || !fd.ocr_product_match)) {
          classes.push("ocr-mismatch")
        }
      }
      return classes
    },
    async loadMoreTransactions() {
      if (this.isLoading || this.isFetchingMore || this.isLastPage) return

      this.isFetchingMore = true
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        page: this.page
      }

      try {
        const response = await TransactionDetailService.getFidelities(this, filters)
        const results = response.results.results

        if (results.length === 0) {
          this.isLastPage = true
        } else {
          this.items.push(...results)
          this.page += 1
          this.totalAmount = response.results.total_amount
          this.totalQuantity = response.results.total_quantity || 0
          this.totalByProduct = response.results.total_by_product || []
        }
      } catch (error) {
        if (error?.response?.status === 404) this.isLastPage = true
        console.error("Erreur chargement transactions", error)
      } finally {
        this.isFetchingMore = false
        this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() })
      }
    },
    exportToPdf() {
      this.isExportingPdf = true
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        no_pagination: true
      }

      TransactionDetailService.getFidelities(this, filters)
        .then(response => {
          const items = response.results?.results || response.results || []
          if (items.length === 0) {
            this.send(this.$t("messages.warning.no_data_to_export"), "warning")
            return
          }

          const doc = new jsPDF("landscape")
          const title = `Liste des transactions - ${new Date().toLocaleDateString()}`
          doc.setFontSize(14)
          doc.text(title, 14, 15)

          const exportData = items.map((item, index) => [
            index + 1,
            item.transaction_number || "N/A",
            this.getComanyName(item),
            item.vehicle?.registration_number || item.user?.phone_number || "N/A",
            this.getUserOrDriverName(item),
            this.formatNumber(item.amount),
            item.product || "N/A",
            this.formatNumber(item.quantity),
            this.toDatetime(item.created_at),
            item.shop || "N/A",
            item.status || "Effectué",
            item.shop ? `${item.shop || ""}`.trim() : "N/A"
          ])

          autoTable(doc, {
            head: [[
              "#",
              "N° Transaction",
              "Société",
              "Client / Matricule",
              "Nom & Prénom",
              "Montant (XOF)",
              "Produit",
              "Quantité (L)",
              "Date",
              "Station",
              "Statut",
              "Effectué par"
            ]],
            body: exportData,
            startY: 20,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 92, 185] }
          })

          doc.save(`transactions_${new Date().toISOString().slice(0, 10)}.pdf`)
          this.send(this.$t("Téléchargé avec succès"), "success")
        })
        .catch(err => {
          console.error("Erreur export PDF :", err)
          this.send(this.$t("messages.error.export_failed"), "error")
        })
        .finally(() => {
          this.isExportingPdf = false
        })
    },
    handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY
      const bottom = document.body.offsetHeight - 300
      if (scrollPosition >= bottom) {
        this.loadMoreTransactions()
      }
    },
    onSearchChange() {
      this.getTransactionDebounced()
    },
    formatDateToString(date) {
      return date.toISOString().split("T")[0]
    },

    fetchFieldTypes() {
      this.isLoading = true
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        page: this.page
      }
      TransactionDetailService.getFidelities(this, filters)
        .then(response => {
          this.items = [...response.results.results]
          this.page = 2
          this.isLastPage = response.results.results.length === 0

          this.totalAmount = response.results.total_amount
          this.totalQuantity = response.results.total_quantity || 0
          this.totalByProduct = response.results.total_by_product || []
        })
        .catch(err => {
          if (err.response && err.response.status === 403) {
            this.$store.commit("app/message", {
              msg: this.$t("messages.error.permission_denied"),
              color: "error"
            })
          }
        })
        .finally(() => {
          this.isLoading = false
          this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() })
        })
    },
    fetchProductsTypes() {
      ProductDetailService.getprincipalAll().then(response => {
        this.products = response.results
      })
    },
    fetchShopTypes() {
      ShopDetailService.getprincipalAll(this).then(response => {
        this.shops = response
      })
    },
    exportToExcel() {
      this.isExportingExcel = true
      const filters = {
        search: this.search,
        startDate: this.startDate,
        endDate: this.endDate,
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        no_pagination: true
      }

      TransactionDetailService.getFidelities(this, filters)
        .then(response => {
          const items = response.results
          if (!items || items.length === 0) {
            this.send(this.$t("messages.warning.no_data_to_export"), "warning")
            return
          }

          const rows = [[
            "N° Transaction",
            "Société",
            "N° Client / Matricule",
            "Nom & Prénom",
            "Montant (XOF)",
            "Produit",
            "Quantité (L)",
            "Date de création",
            "Station",
            "Statut",
            "Effectué par"
          ]]

          items.forEach(item => {
            const montant = typeof item.amount === "number" ? item.amount : parseFloat(item.amount) || 0
            const quantite = typeof item.quantity === "number" ? item.quantity : parseFloat(item.quantity) || 0

            rows.push([
              item.transaction_number || "",
              this.getComanyName(item) || "",
              item.vehicle?.registration_number || item.user?.phone_number || "",
              this.getUserOrDriverName(item),
              montant,
              item.product || "",
              quantite,
              this.toDatetime(item.created_at) || "",
              item.shop || "",
              item.status || "Effectué",
              item.shop ? `${item.shop || ""}`.trim() : ""
            ])
          })

          const worksheet = XLSX.utils.aoa_to_sheet(rows)
          const workbook = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions")

          const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
          const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          })

          saveAs(blob, `transactions_${new Date().toISOString().slice(0, 10)}.xlsx`)
          this.send(this.$t("Téléchargé avec succès"), "success")
        })
        .catch(err => {
          console.error("Erreur export Excel :", err)
          this.send(this.$t("messages.error.export_failed"), "error")
        })
        .finally(() => {
          this.isExportingExcel = false
        })
    },

    formatNumber(value) {
      if (!value) return "0"
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    },
    isVehicle(item) {
      if (item.is_vehicle) {
        return item.user.first_name
      }
      return item.user.phone_number
    },
    getUserOrVehicleContact(item) {
      if (item.vehicle) {
        return item.vehicle.registration_number
      }
      return item.user?.phone_number || "N/A"
    },
    getComanyName(item) {
      if (item.vehicle) {
        return item.vehicle.company
      }
      if (item.user.phone_number === "+2250000000009") {
        return item.created_by.company.name
      }
      return ""
    },
    getComanyAvalanche(item) {
      if (item.vehicle) {
        return item.vehicle.company_avalanche_id
      }
      if (item.user.phone_number === "+2250000000009") {
        return item.created_by.company.company_avalanche_id
      }
      return ""
    },
    getUserOrDriverName(item) {
      const name = item?.user?.fullname?.trim()
      return (
        (name && name.length ? name : item?.user?.first_name + " " + item?.user?.last_name) ||
        ""
      ).toLocaleUpperCase("fr-FR")
    },
    openItemUrl(item) {
      window.open(item.image, "_blank")
    },
    handleStatusComponent() {
      return "Accepted"
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    },
    applyFilters() {
      this.page = 1
      this.isLastPage = false
      this.items = []
      this.fetchFieldTypes()
    },
    exportStation() {
      this.stationDialog = false
      this.isExportingStation = true

      const sel = new Date(this.stationDate)
      const start = new Date(sel)
      start.setHours(8, 0, 0, 0)
      const end = new Date(sel)
      end.setDate(end.getDate() + 1)
      end.setHours(8, 0, 0, 0)

      const filters = {
        search: this.search,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        product: this.selectedProduct,
        company: this.selectedCompany,
        shop: this.selectedShop,
        department: this.department?.id || null,
        no_pagination: true
      }

      TransactionDetailService.getFidelities(this, filters)
        .then(response => {
          const exportDateStr = sel.toISOString().split("T")[0]
          const rawItems = response.results || []

          const items = rawItems.filter(it => {
            const companyName = this.getComanyName(it)
            const hasCompany = !!(companyName && String(companyName).trim())
            const hasStationIdent = !!(it.shop_avalanche_id && String(it.shop_avalanche_id).trim())
            return hasCompany && hasStationIdent
          })

          if (items.length === 0) {
            this.send(this.$t("Aucune donnée a exporter"), "warning")
            return
          }

          const exportData = items.map(item => ({
            "N° Transaction": item.transaction_number || "N/A",
            Société: this.getComanyName(item) || "N/A",
            Station: item.shop || "N/A",
            "N° Client": item.vehicle?.registration_number || item.user?.phone_number || "N/A",
            "Nom & Prénom": this.getUserOrDriverName(item),
            "Montant (XOF)": Number(item.amount) || 0,
            Produit: item.product || "N/A",
            "Quantité (L)": Number(item.quantity) || 0,
            "Date création": this.toDatetime(item.created_at),
            "Date Export Avalanche": exportDateStr,
            Statut: item.status || "Effectué",
            "Effectué par": item.shop ? `${item.shop}`.trim() : "N/A",
            "COMPANY AVALANCHE IDENT": this.getComanyAvalanche(item),
            "STATION AVALANCHE IDENT": item.shop_avalanche_id,
            "UNIT PRICE": Number(item.product_price) || 0
          }))

          const ws = XLSX.utils.json_to_sheet(exportData)
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, "Station")
          const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" })
          const blob = new Blob([wbout], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          })
          saveAs(blob, `station_export_${this.stationDate}.xlsx`)
          this.send(this.$t("Téléchargé avec succès"), "success")
        })
        .catch(err => {
          this.send(this.$t("messages.error.export_failed"), "error")
          console.error(err)
        })
        .finally(() => {
          this.isExportingStation = false
        })
    },

    // Helpers pour les chips de statut (mobile)
    statusLabel(it) {
      const s = (it.status || "").toLowerCase()
      if (s === "pending") return this.$t("En attente")
      if (s === "accepted" || s === "done" || s === "effectué") return this.$t("Effectué")
      if (s === "canceled" || s === "rejected") return this.$t("Annulé")
      return s || "—"
    },
    statusColor(it) {
      const s = (it.status || "").toLowerCase()
      if (s === "pending") return "warning"
      if (s === "accepted" || s === "done" || s === "effectué") return "success"
      if (s === "canceled" || s === "rejected") return "error"
      return "primary"
    }
  },
  mounted() {
    this.getTransactionDebounced = debounce(() => {
      this.page = 1
      this.items = []
      this.isLastPage = false
      this.fetchFieldTypes()
    }, 500)

    this.loadMoreTransactions()
    window.addEventListener("scroll", this.handleScroll)
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll)
  }
}
</script>

<style scoped>
/* ===== Container principal ===== */
.transaction-detail-container {
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
}

/* ===== En-tête de page ===== */
.mobile-page-header {
  background: #ffffff;
}

/* ===== Filtres ===== */
.search-field {
  border-radius: 12px;
}

.export-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}

/* ===== Section stats ===== */
.stats-section {
  margin-top: 10px;
  margin-bottom: 0px;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}

.stats-wrapper {
  padding: 0 5px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

/* ===== Carte principale (Total) ===== */
.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 0px;
  padding-bottom: 0px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  position: relative;
  overflow: hidden;
}

.stat-card-main::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
}

.stat-card-main:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(25, 118, 210, 0.4) !important;
}

.stat-card-main .stat-secondary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

/* ===== Cartes produits ===== */
.stat-card-product {
  border-radius: 16px !important;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 0px;
  padding-bottom: 0px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}

.stat-card-product:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
  border-color: #90caf9;
}

.stat-secondary-product {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding-top: 12px;
  border-top: 1px solid #e0e0e0;
}
/* ===== Stats mobile ===== */
.stat-card-mobile {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 16px !important;
  border: none;
  box-shadow: 0 8px 20px rgba(25, 118, 210, 0.3) !important;
}

.stat-card-mobile .v-card__text {
  color: white !important;
}

.stat-label-mobile {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  margin-bottom: 6px;
}

.stat-value-mobile {
  font-size: 28px;
  font-weight: 800;
  line-height: 1.2;
}

.stat-unit-mobile {
  font-size: 16px;
  font-weight: 600;
  opacity: 0.85;
}

.stat-icon-mobile {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.stat-secondary-mobile {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
}

/* ===== Cartes mobiles ===== */
.transaction-card {
  background: #ffffff !important;
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  animation: fadeIn 0.4s ease-out;
}

.transaction-card:active {
  transform: scale(0.98);
}

.company {
  font-weight: 700;
  font-size: 16px;
  color: #212121;
}

/* Grid info mobile */
.grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.cell {
  padding: 10px;
  background: #fafafa;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}

.cell .label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #757575;
  font-weight: 700;
  margin-bottom: 3px;
}

.cell .value {
  font-size: 13px;
  font-weight: 700;
  color: #212121;
  word-break: break-word;
}

/* ===== Overlays d'export ===== */
.export-overlay-content {
  text-align: center;
  padding: 32px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  color: #212121;
}

/* ===== Animations ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== Responsive ===== */
@media (max-width: 960px) {
  .stats-container {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }

  .stat-value-mobile {
    font-size: 24px;
  }

  .company {
    font-size: 15px;
  }
}

@media (max-width: 360px) {
  .stat-value-mobile {
    font-size: 20px;
  }
}

/* ===== Highlight des lignes fidélité remboursées ===== */
::v-deep .v-data-table__wrapper tbody tr.fidelity-refund > td {
  background-color: #c9f9c3 !important;
  border-top: 1px solid #c9f9c3 !important;
  border-bottom: 1px solid #c9f9c3 !important;
}

::v-deep .v-data-table__wrapper tbody tr.fidelity-refund > td:first-child {
  box-shadow: inset 4px 0 0 0 #9ace94;
}

/* Cartes mobile fidélité encaissement */
.fidelity-refund-card {
  background-color: #c9f9c3 !important;
  border-left: 4px solid #9ace94 !important;
}

/* ===== Highlight OCR mismatch ===== */
::v-deep .v-data-table__wrapper tbody tr.ocr-mismatch > td {
  background-color: #fffde7 !important;
}

::v-deep .v-data-table__wrapper tbody tr.ocr-mismatch > td:first-child {
  box-shadow: inset 4px 0 0 0 #ffe082;
}

/* ===== Highlight OCR auto-corrected ===== */
::v-deep .v-data-table__wrapper tbody tr.ocr-auto-corrected > td {
  background-color: #f1f8e9 !important;
}

::v-deep .v-data-table__wrapper tbody tr.ocr-auto-corrected > td:first-child {
  box-shadow: inset 4px 0 0 0 #7cb342;
}
</style>
